import {
  addMonths,
  areIntervalsOverlapping,
  differenceInMonths,
  format,
  isWithinInterval,
  parseISO,
  startOfMonth,
  subMonths
} from "date-fns";
import {sum, sumByKey, tailwindColor} from "@/Utilities/Utilities";
import {Chart} from "@/Components";
import React from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";

export const prepareEntryData = (setCurrentTeamId, teamOptions, endDate, startDate, teams, filters, setGraphData, setPieData) => {
  return (selectedTeamId) => {

    setCurrentTeamId(parseInt(selectedTeamId));

    let includeTeams = teamOptions.map(t => t.value);

    if (selectedTeamId) {
      includeTeams = [parseInt(selectedTeamId)];
    }

    if (includeTeams.length === 0) {
      return;
    }

    // Deep copy default arrays, very nice ಠ_ಠ
    let rawData = JSON.parse(JSON.stringify(defaultData.raw));
    let _pieData = JSON.parse(JSON.stringify(defaultData.pie));
    let _graphData = JSON.parse(JSON.stringify(defaultData.graph));

    const monthsDiff = differenceInMonths(endDate, startDate);

    includeTeams.map(teamId => {

      const teamIndex = teams.data.findIndex(t => t.id === teamId);

      if (teamIndex === -1) {
        return;
      }

      const team = teams.data[teamIndex];

      // Only include locations selected in filter if available
      if (filters.location && team.location_place_id !== filters.location) {
        return;
      }

      team?.activities?.map(activity => {
        if (!activity.entries || activity.entries.length === 0) {
          return;
        }

        // Only include activities selected in filter if available
        if (filters.activity && activity.id !== parseInt(filters.activity)) {
          return;
        }

        activity.entries.map(entry => {

          // Check if any of our dates between start date and end date overlapps the filter dates
          if (!isWithinInterval(parseISO(entry.end_date), {start: startDate, end: endDate})) {
            if (team.report_frequency === 1) {
              return;
            } else {
              // We must match against both start and end dates for teams with reporting frequency other than monthly
              if (!areIntervalsOverlapping(
                {start: parseISO(entry.start_date), end: parseISO(entry.end_date)},
                {start: startDate, end: endDate}
              )) {
                return;
              }
            }
          }

          let value;

          if (filters.gas === 'co2e') {
            value = entry[filters.format] || false;
          } else {
            value = entry.value * entry[`${filters.gas}_factor`] || false;
          }

          if (value === false) {
            return;
          }

          let formattedEntry = {
            label: entry.type === 'Calculator' ? `Calculators: ${activity.name}` : activity.name,
            value: value / team.report_frequency,
            start_date: parseISO(entry.start_date),
            end_date: parseISO(entry.end_date),
            activity_id: activity.id,
            location_place_id: team.location_place_id
          };

          if (team.report_frequency !== 1) {
            for (let m = 1; m < team.report_frequency; m++) {
              let splitEntry = {...formattedEntry};
              splitEntry.end_date = subMonths(formattedEntry.end_date, m);

              if (isWithinInterval(splitEntry.end_date, {start: startDate, end: endDate})) {
                rawData[activity.scope].push(splitEntry);
              }

            }
          }

          if (isWithinInterval(formattedEntry.end_date, {start: startDate, end: endDate})) {
            rawData[activity.scope].push(formattedEntry);
          }
        })
      });
    });

    Object.keys(rawData).map(i => {

      // Build the array using the format of YYYY_M keys to be able to match against end_date of entry
      if (_graphData[i].length === 0) {
        for (let monthOffset = 0; monthOffset <= monthsDiff; monthOffset++) {
          const date = addMonths(startOfMonth(startDate), monthOffset);
          const dateKey = format(date, 'yyyy_M');

          const obj = {};
          obj[dateKey] = {entries: [], sum: 0, label: format(date, 'MMM, yy')};

          _graphData[i] = {..._graphData[i], ...obj};
        }
      }

      // No need to processing data keep if no result here
      if (rawData[i].length === 0) {
        return;
      }

      _pieData[i] = sumByKey(rawData[i], 'value');

      rawData[i].map(item => {
        const itemDateKey = format(item.end_date, 'yyyy_M');

        if (!_graphData[i].hasOwnProperty(itemDateKey)) {
          return;
        }

        _graphData[i][itemDateKey].entries.push(item);
        _graphData[i][itemDateKey].sum += item.value;
      });
    });

    setGraphData(_graphData);
    setPieData(_pieData);
  };
}

export const getTeamOptions = (teams) => teams?.data?.map(team => {
  if (team?.activities?.length > 0) {

    let activities = [];

    team.activities.map(activity => {
      if (!activity.entries || activity.entries.length === 0) {
        return;
      }

      activities.push({value: activity.id, label: activity.name});
    });

    const location = {value: team.location_place_id, label: team.location};

    return {value: team.id, label: team.name, activities: activities, location};
  }
}).filter(t => t);


export const dataEntryFilterHelpers = (filters, setFilters, currentTeamId, teamOptions) => {
  const applyFilter = (type, value) => {

    if (filters[type] === undefined || filters[type] === value) {
      return;
    }

    setFilters(prevValues => {
      return {...prevValues, [type]: value}
    });
  }

  const getFilterOptions = (type) => {

    let list;
    let label = 'All activities';
    let team = currentTeamId ? teamOptions.find(t => t.value === currentTeamId) : null;

    if (type === 'activities') {
      list = team ? team.activities : teamOptions.map(t => t.activities.filter(a => a.value !== 0)).flat();
    } else if (type === 'locations') {
      list = team ? [team.location] : teamOptions.map(t => t.location);
      label = 'All locations';

      // Filter out duplicate values magic based on label
      list = [...new Map(list.map(item => [item['label'], item])).values()];
    }

    return [{label: label, value: ""}, ...list];
  }
  return {applyFilter, getFilterOptions};
}

export const gases = [
  {label: 'CO₂e', value: 'co2e'},
  {label: 'CO₂', value: 'co2'},
  {label: 'CH₄', value: 'ch4'},
  {label: 'N₂O', value: 'n2o'}
];

const defaultData = {
  raw: {
    0: [], 1: [], 2: [], 3: []
  },
  graph: {
    0: [], 1: [], 2: [], 3: []
  },
  pie: [
    0, 0, 0, 0
  ]
};

export const chartColors = [
  tailwindColor('bg-primary'),
  tailwindColor('bg-apricot'),
  tailwindColor('bg-purple'),
  tailwindColor('bg-slate-light'),
];

// Map graph index to the actual graphData state index
const transformIndex = [1, 2, 3, 0];

export const EntriesBarChart = ({graphData}) => {

  window.globalGasType = window.globalGasType || 'CO₂';

  return <Chart
    height={350}
    width={500}
    config={{
      type: 'bar',
      data: {
        labels: Object.keys(graphData[0]).map(i => graphData[0][i].label),
        datasets: [
          {
            label: 'Scope 1',
            barPercentage: 0.4,
            data: Object.keys(graphData[1]).map(i => graphData[1][i].sum),
            backgroundColor: chartColors[0],
          },
          {
            label: 'Scope 2',
            barPercentage: 0.4,
            data: Object.keys(graphData[2]).map(i => graphData[2][i].sum),
            backgroundColor: chartColors[1],
          },
          {
            label: 'Scope 3',
            barPercentage: 0.4,
            data: Object.keys(graphData[3]).map(i => graphData[3][i].sum),
            backgroundColor: chartColors[2],
          },
          {
            label: 'Out of scope',
            barPercentage: 0.4,
            data: Object.keys(graphData[0]).map(i => graphData[0][i].sum),
            backgroundColor: chartColors[3],
          },
        ],
      },
      elements: {
        point: {
          radius: 2,
        },
      },
      options: {
        scales: {
          x: {
            stacked: true,
          },
          y: {
            stacked: true
          }
        },
        plugins: {
          legend: {
            display: false
          },
          title: {
            display: true,
            text: `KG ${window.globalGasType}`,
            align: 'start',
            padding: {
              bottom: 30
            }
          },
          tooltip: {
            padding: 15,
            displayColors: false,
            backgroundColor: (context) => {
              return context.tooltip.labelColors[0].backgroundColor;
            },
            titleColor: (context) => {
              return context.tooltip.labelTextColors[0];
            },
            callbacks: {
              title: (context) => {

                const obj = Object.keys(window.globalGraphData).length > 0 ? window.globalGraphData : graphData;

                const graphKey = transformIndex[context[0].datasetIndex];
                const graphDataArrayKeys = Object.keys(obj[graphKey]);

                const totalSum = sum(
                  Object.keys(obj).map(i => (
                    obj[i][graphDataArrayKeys[context[0].dataIndex]].sum
                  ))
                );

                return `${context[0].dataset.label}, ${Math.round((context[0].raw / totalSum) * 100)} %`;
              },
              labelTextColor: (context) => {
                return (context.datasetIndex === 1 || context.datasetIndex === 3) ? 'black' : 'white';
              },
              label: (context) => {

                const obj = Object.keys(window.globalGraphData).length > 0 ? window.globalGraphData : graphData;

                const graphKey = transformIndex[context.datasetIndex];
                const graphDataArrayKeys = Object.keys(obj[graphKey]);
                const item = obj[graphKey][graphDataArrayKeys[context.dataIndex]];

                // const totalSum = sum(
                //   Object.keys(obj).map(i => (
                //     obj[i][graphDataArrayKeys[context.dataIndex]].sum
                //   ))
                // );

                return item.entries.map(entry => {
                  return `${entry.label}, ${Math.round(entry.value)} KG ${window.globalGasType}`;
                  // return `${entry.label} ${Math.round((entry.value / totalSum) * 100)} %`;
                });
              },
            },
          }
        }
      }
    }}
  />;
}

export const EntriesPieChart = ({pieData, graphData}) => {

  window.globalGasType = window.globalGasType || 'CO₂';

  return <Chart
    width={350}
    height={350}
    config={{
      type: 'pie',
      data: {
        labels: [
          'Scope 1',
          'Scope 2',
          'Scope 3',
          'Out of scope'
        ],
        datasets: [
          {
            label: `KG ${window.globalGasType}`,
            borderJoinStyle: 'round',
            borderColor: tailwindColor('bg-background'),
            hoverBorderColor: tailwindColor('bg-background'),
            hoverOffset: 4,
            borderRadius: 8,
            borderWidth: 4,
            // fill: 'origin',
            backgroundColor: chartColors,
            datalabels: {
              color: ['white', 'black', 'white', 'black']
            },
            data: [
              Math.round(pieData[1] / sum(pieData) * 100),
              Math.round(pieData[2] / sum(pieData) * 100),
              Math.round(pieData[3] / sum(pieData) * 100),
              Math.round(pieData[0] / sum(pieData) * 100),
            ],
          },
        ],
      },
      options: {
        plugins: {
          datalabels: {
            formatter: function (value, context) {
              return (value === 0 || value === "") ? "" : `${value} %`;
            }
          },
          legend: {
            align: 'left',
            labels: {
              align: 'start',
              boxWidth: 16,
              boxHeight: 16,
            }
          },
          tooltip: {
            padding: 15,
            displayColors: false,
            backgroundColor: (context) => {
              return context.tooltip.labelColors[0].backgroundColor;
            },
            titleColor: (context) => {
              return context.tooltip.labelTextColors[0];
            },
            callbacks: {
              title: (context) => {
                return context[0].label;
              },
              labelTextColor: (context) => {
                return (context.datasetIndex === 1 || context.datasetIndex === 3) ? 'black' : 'white';
              },
              label: (context) => {

                let labels = {};
                const obj = Object.keys(window.globalGraphData).length > 0 ? window.globalGraphData : graphData;
                const graphKey = transformIndex[context.dataIndex];

                const totalSum = sum(
                  Object.keys(obj[graphKey]).map(i => (
                    obj[graphKey][i].sum
                  ))
                );

                Object.keys(obj[graphKey]).map(i => {
                  obj[graphKey][i].entries.map(entry => {

                    if (typeof labels[entry.activity_id] === 'undefined') {
                      labels[entry.activity_id] = {label: entry.label, sum: 0};
                    }

                    labels[entry.activity_id].sum += entry.value;
                  });
                });

                return Object.keys(labels).map(i => {
                  return `${labels[i].label}, ${Math.round(labels[i].sum)} KG ${window.globalGasType}`;
                  // return `${labels[i].label} ${Math.round((labels[i].sum / totalSum) * 100)} %`;
                });
              },
            },
          }
        }
      }
    }}
    plugins={[ChartDataLabels]}
  />;
}
